import { useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Modal,
  Popconfirm,
  Progress,
  Radio,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CloudServerOutlined,
  DeleteOutlined,
  PushpinFilled,
  PushpinOutlined,
  RedoOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { supabase } from '@/lib/supabase'
import { formatCST } from '@/lib/time'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { DebouncedSearchInput } from '@/components/DebouncedSearchInput'
import {
  TIER_LABELS,
  TRANSCODE_STATUS_COLORS,
  TRANSCODE_STATUS_LABELS,
  formatBytes,
  summarizeEnqueue,
} from '@/lib/transcodes'
import type {
  TranscodeListResult,
  TranscodeRow,
  TranscodeStats,
  TranscodeStatus,
} from '@/lib/transcodes'
import { enqueueTranscodes } from '@/lib/transcodes-rpc'

export const Route = createFileRoute('/_authed/transcodes')({
  component: TranscodesPage,
})

const PAGE_SIZE = 50
// 与 worker 轮询节奏一致：状态变化 10 秒内可见
const POLL_MS = 10_000

// 存储水位（与 backend mark_evictions 对齐）：
//   低水位 90% = 1.8TB，驱逐清到这条线；满载稳态停在 90–100% 属设计内，不该飘红。
//   危险 98% = 驱逐跟不上/越过高水位，才是真告警。
const LOW_WATER_PCT = 90
const DANGER_PCT = 98

type TabKey = 'all' | TranscodeStatus

const TAB_ORDER: TabKey[] = [
  'all',
  'queued',
  'transcoding',
  'ready',
  'evicting',
  'evicted',
  'failed',
]

// RPC 错误码 → 用户可读文案（与 backend admin RPC 的 error 短码一一对应）
const RPC_ERROR_LABELS: Record<string, string> = {
  forbidden: '没有管理员权限',
  not_found: '任务不存在或已被删除',
  invalid_status: '任务状态已变化，请等列表刷新后重试',
  too_many_ids: '单次最多入队 100 集',
  empty: '没有可入队的剧集',
}

function rpcErrorText(e: Error): string {
  return RPC_ERROR_LABELS[e.message] ?? e.message
}

// 各状态 tab 的空态文案：说清"为什么是空的 + 接下来会发生什么"
const EMPTY_META: Record<TabKey, { title: string; hint: string }> = {
  all: {
    title: '还没有转码任务',
    hint: '从番剧的剧集管理页入队，或等待自动准入（热度/季度新番）',
  },
  queued: {
    title: '队列已清空',
    hint: 'worker 每 10 秒轮询一次，新任务入队后会自动被认领',
  },
  transcoding: {
    title: '没有正在转码的任务',
    hint: '队列中的任务会被在线 worker 自动认领',
  },
  ready: {
    title: '还没有已就绪的产物',
    hint: '转码完成后会出现在这里，并参与存储预算结算',
  },
  evicting: {
    title: '没有等待移除的任务',
    hint: '超出 2TB 预算或手动移除时，产物会先进入等待移除',
  },
  evicted: {
    title: '没有已移除的任务',
    hint: '已移除的剧集热度回升后会自动重新入队',
  },
  failed: {
    title: '没有失败的任务',
    hint: '转码链路一切正常',
  },
}

const NUM_STYLE = { fontVariantNumeric: 'tabular-nums' } as const

function statusTime(row: TranscodeRow): string | null {
  switch (row.status) {
    case 'ready':
      return row.ready_at
    case 'evicting':
      return row.evicting_at
    case 'evicted':
      return row.evicted_at
    default:
      return row.queued_at
  }
}

function TranscodesPage() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const [tab, setTab] = useState<TabKey>('all')
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [pinTarget, setPinTarget] = useState<TranscodeRow | null>(null)

  /* ── 统计卡（与列表独立 query，互不拖累） ── */
  const statsQuery = useQuery({
    queryKey: ['admin_transcode_stats'],
    refetchInterval: POLL_MS,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_transcode_stats')
      if (error) throw error
      const r = data as TranscodeStats
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
  })

  /* ── 队列列表 ── */
  const listQuery = useQuery({
    queryKey: ['admin_transcodes', { tab, keyword, page }],
    placeholderData: keepPreviousData,
    refetchInterval: POLL_MS,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_transcodes', {
          p_status: tab === 'all' ? null : tab,
          p_keyword: keyword || null,
          p_anime_id: null,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        })
      if (error) throw error
      const r = data as TranscodeListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return { rows: r.rows ?? [], total: r.total ?? 0, counts: r.counts ?? {} }
    },
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin_transcodes'] })
    void qc.invalidateQueries({ queryKey: ['admin_transcode_stats'] })
  }

  /* ── mutations ── */
  const enqueueMutation = useMutation({
    mutationFn: enqueueTranscodes,
    onSuccess: (out) => {
      message.success(summarizeEnqueue(out))
      invalidate()
    },
    onError: (e: Error) => message.error(`入队失败：${rpcErrorText(e)}`),
  })

  const evictMutation = useMutation({
    mutationFn: async (episodeId: number) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('evict_transcode', { p_episode_id: episodeId })
      if (error) throw error
      if (!data.ok) throw new Error(data.error ?? 'unknown')
    },
    onSuccess: () => {
      message.success('已标记移除，worker 将清理 R2 产物')
      invalidate()
    },
    onError: (e: Error) => message.error(`移除失败：${rpcErrorText(e)}`),
  })

  const pinMutation = useMutation({
    mutationFn: async (vars: {
      episodeId: number
      pinned: boolean
      expiresAt: string | null
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('set_transcode_pin', {
          p_episode_id: vars.episodeId,
          p_pinned: vars.pinned,
          p_expires_at: vars.expiresAt,
        })
      if (error) throw error
      if (!data.ok) throw new Error(data.error ?? 'unknown')
    },
    onSuccess: (_d, vars) => {
      setPinTarget(null)
      message.success(vars.pinned ? '已设置 Pin 豁免' : '已取消 Pin')
      invalidate()
    },
    onError: (e: Error) => message.error(`Pin 操作失败：${rpcErrorText(e)}`),
  })

  /* ── tab 选项（计数来自 list 返回的 counts；首载未出数前不渲染数字防 0 闪烁） ── */
  const counts = listQuery.data?.counts ?? {}
  const allCount = Object.values(counts).reduce((s, n) => s + n, 0)
  const hasCounts = listQuery.data != null
  const tabOptions = TAB_ORDER.map((key) => {
    const count = key === 'all' ? allCount : (counts[key] ?? 0)
    return {
      value: key,
      label: (
        <span>
          {key === 'all' ? '全部' : TRANSCODE_STATUS_LABELS[key]}
          {hasCounts && (
            <span
              style={{
                marginLeft: 4,
                fontSize: 12,
                ...NUM_STYLE,
                color:
                  key === 'failed' && count > 0
                    ? 'var(--ant-color-error-text)'
                    : 'var(--ant-color-text-tertiary)',
              }}
            >
              {count}
            </span>
          )}
        </span>
      ),
    }
  })

  /* ── 列 ── */
  const columns = useMemo<ColumnsType<TranscodeRow>>(
    () => [
      {
        title: '剧集',
        key: 'episode',
        render: (_, row) => (
          <Space orientation="vertical" size={0}>
            <Link
              to="/anime/$animeId/episodes"
              params={{ animeId: String(row.anime_id) }}
            >
              {row.anime_title ?? `番剧 #${row.anime_id}`}
            </Link>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              第 {row.episode_number} 集
              {row.episode_title ? ` · ${row.episode_title}` : ''}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '状态',
        key: 'status',
        render: (_, row) => {
          const tag = (
            <Tag color={TRANSCODE_STATUS_COLORS[row.status]}>
              {TRANSCODE_STATUS_LABELS[row.status]}
            </Tag>
          )
          if (row.status === 'transcoding') {
            return (
              <Tooltip
                title={`worker: ${row.claimed_by ?? '?'} · 心跳 ${formatCST(row.heartbeat_at, 'HH:mm:ss')}`}
              >
                {tag}
              </Tooltip>
            )
          }
          if (row.status === 'failed' && row.last_error) {
            // 失败原因是排障核心信息：行内直接露出截断文本，hover 看全文
            return (
              <Tooltip title={row.last_error}>
                <Space size={6}>
                  {tag}
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, maxWidth: 200 }}
                    ellipsis
                  >
                    {row.last_error}
                  </Typography.Text>
                </Space>
              </Tooltip>
            )
          }
          return tag
        },
      },
      {
        title: '档位',
        key: 'tier',
        render: (_, row) => <Tag>{TIER_LABELS[row.admission_tier]}</Tag>,
      },
      {
        title: 'Pin',
        key: 'pin',
        render: (_, row) =>
          row.pinned ? (
            <Tooltip
              title={
                row.pin_expires_at
                  ? `豁免至 ${formatCST(row.pin_expires_at)}`
                  : '永久豁免'
              }
            >
              <PushpinFilled style={{ color: 'var(--ant-color-warning)' }} />
            </Tooltip>
          ) : null,
      },
      {
        title: '体积',
        key: 'bytes',
        align: 'right',
        render: (_, row) => (
          <span style={NUM_STYLE}>
            {formatBytes(row.total_bytes ?? row.source_bytes)}
          </span>
        ),
      },
      {
        title: '7 天命中',
        dataIndex: 'hits_7d',
        align: 'right',
        render: (hits: number) =>
          hits === 0 ? (
            // 0 命中的 ready 行是驱逐候选：弱化但保留，不显示为空
            <Typography.Text type="secondary" style={NUM_STYLE}>
              0
            </Typography.Text>
          ) : (
            <span style={NUM_STYLE}>{hits}</span>
          ),
      },
      {
        title: '尝试',
        dataIndex: 'attempts',
        align: 'right',
        render: (attempts: number, row) => (
          <span
            style={{
              ...NUM_STYLE,
              // 3 = 重试上限（fail_transcode 判定 failed 的阈值）；
              // 只在确实失败时标红——ready 行的 3 只是"第三次才成功"，不是问题
              color:
                row.status === 'failed' && attempts >= 3
                  ? 'var(--ant-color-error-text)'
                  : undefined,
            }}
          >
            {attempts}
          </span>
        ),
      },
      {
        title: '时间',
        key: 'time',
        render: (_, row) => (
          <Tooltip
            title={`入队 ${formatCST(row.queued_at, 'YYYY-MM-DD HH:mm:ss')}`}
          >
            <span style={NUM_STYLE}>
              {formatCST(statusTime(row), 'MM-DD HH:mm')}
            </span>
          </Tooltip>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        render: (_, row) => (
          <Space size={4}>
            {(row.status === 'failed' || row.status === 'evicted') && (
              <Popconfirm
                title="重新入队转码？"
                onConfirm={() => enqueueMutation.mutate([row.episode_id])}
              >
                <Button
                  size="small"
                  type="text"
                  icon={<RedoOutlined />}
                  disabled={enqueueMutation.isPending}
                >
                  重试
                </Button>
              </Popconfirm>
            )}
            {row.status === 'ready' && (
              <Popconfirm
                title="移除转码产物？"
                description="将删除 R2 产物并清除 Pin，由 worker 异步清理。"
                okButtonProps={{ danger: true }}
                onConfirm={() => evictMutation.mutate(row.episode_id)}
              >
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={evictMutation.isPending}
                >
                  移除
                </Button>
              </Popconfirm>
            )}
            {row.pinned ? (
              <Button
                size="small"
                type="text"
                icon={<PushpinOutlined />}
                disabled={pinMutation.isPending}
                onClick={() =>
                  pinMutation.mutate({
                    episodeId: row.episode_id,
                    pinned: false,
                    expiresAt: null,
                  })
                }
              >
                解除 Pin
              </Button>
            ) : (
              <Button
                size="small"
                type="text"
                icon={<PushpinOutlined />}
                disabled={pinMutation.isPending}
                onClick={() => setPinTarget(row)}
              >
                Pin
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [enqueueMutation, evictMutation, pinMutation],
  )

  const stats = statsQuery.data
  // 保留一位小数：2TB 预算下几个 GB 也别显示成 0%
  const usedPercent =
    stats?.used_bytes != null && stats.budget_bytes
      ? Number(((stats.used_bytes / stats.budget_bytes) * 100).toFixed(1))
      : 0
  const queueDepth =
    (stats?.counts?.queued ?? 0) + (stats?.counts?.transcoding ?? 0)
  // 有活没人干 = 队列停滞，是这页最需要被看见的信号
  const workersStalled =
    stats != null && stats.active_workers === 0 && queueDepth > 0
  const suffixStyle = {
    fontSize: 14,
    color: 'var(--ant-color-text-tertiary)',
    ...NUM_STYLE,
  } as const
  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0
  const emptyContent = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <Space orientation="vertical" size={4}>
          <Typography.Text strong>
            {keyword ? '没有匹配的番剧' : EMPTY_META[tab].title}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {keyword
              ? '换个关键字，或清空搜索查看全部任务'
              : EMPTY_META[tab].hint}
          </Typography.Text>
        </Space>
      }
    />
  )

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      {/* ── 加载失败兜底（统计与列表任一挂掉都给出口） ── */}
      {(statsQuery.isError || listQuery.isError) && (
        <Alert
          type="error"
          showIcon
          message="转码数据加载失败"
          description={rpcErrorText(
            (listQuery.error ?? statsQuery.error) as Error,
          )}
          action={
            <Button
              size="small"
              onClick={() => {
                void statsQuery.refetch()
                void listQuery.refetch()
              }}
            >
              重试
            </Button>
          }
        />
      )}

      {/* ── 统计卡 ── */}
      {/* stretch + 卡片 100% 高：首卡多一条进度条，其余三张拉齐到同一高度 */}
      <Row gutter={[12, 12]} align="stretch">
        <Col xs={12} lg={6}>
          <Card
            size="small"
            className="transcodes-stat-card"
            style={{ height: '100%' }}
          >
            <Statistic
              title="存储用量 / 预算"
              loading={statsQuery.isLoading}
              value={formatBytes(stats?.used_bytes)}
              styles={{ content: NUM_STYLE }}
              suffix={
                <span style={suffixStyle}>
                  / {formatBytes(stats?.budget_bytes)}
                  {stats != null && ` · ${usedPercent}%`}
                </span>
              }
            />
            {stats != null && (
              // 满载稳态停在 90–100%（低水位 1.8TB ~ 高水位 2TB）：
              // 90% 虚线 = 驱逐目标线，左侧为余量；只有 >=98%（驱逐跟不上）才飘红
              <div style={{ position: 'relative', marginTop: 8 }}>
                <Progress
                  percent={usedPercent}
                  size="small"
                  showInfo={false}
                  strokeColor={
                    usedPercent >= DANGER_PCT
                      ? 'var(--ant-color-error)'
                      : usedPercent >= LOW_WATER_PCT
                        ? 'var(--ant-color-warning)'
                        : undefined
                  }
                />
                <Tooltip
                  title={`低水位 ${formatBytes((stats.budget_bytes ?? 0) * 0.9)}（90%）——驱逐清到此线，左侧为余量；越过即进入满载稳态带`}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: `${LOW_WATER_PCT}%`,
                      top: -2,
                      bottom: -2,
                      width: 8,
                      marginLeft: -4,
                      display: 'flex',
                      justifyContent: 'center',
                      cursor: 'help',
                    }}
                  >
                    <div
                      style={{
                        width: 0,
                        height: '100%',
                        borderLeft: '2px dashed var(--ant-color-text-tertiary)',
                      }}
                    />
                  </div>
                </Tooltip>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card
            size="small"
            className="transcodes-stat-card"
            style={{ height: '100%' }}
          >
            <Statistic
              title="队列深度"
              loading={statsQuery.isLoading}
              value={queueDepth}
              styles={{ content: NUM_STYLE }}
              suffix={
                <span style={suffixStyle}>
                  · 转码中 {stats?.counts?.transcoding ?? 0}
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card
            size="small"
            className="transcodes-stat-card"
            style={{ height: '100%' }}
          >
            <Statistic
              title="在线 worker"
              loading={statsQuery.isLoading}
              prefix={<CloudServerOutlined />}
              value={stats?.active_workers ?? 0}
              styles={{
                content: {
                  ...NUM_STYLE,
                  color: workersStalled
                    ? 'var(--ant-color-warning-text)'
                    : undefined,
                },
              }}
              suffix={
                workersStalled ? (
                  <span
                    style={{
                      ...suffixStyle,
                      color: 'var(--ant-color-warning-text)',
                    }}
                  >
                    队列停滞
                  </span>
                ) : undefined
              }
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card
            size="small"
            className="transcodes-stat-card"
            style={{ height: '100%' }}
          >
            <Statistic
              title="7 天命中 / 未命中"
              loading={statsQuery.isLoading}
              value={stats?.hits_7d ?? 0}
              styles={{ content: NUM_STYLE }}
              suffix={
                <span style={suffixStyle}>/ {stats?.misses_7d ?? 0}</span>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* ── 列表 ── */}
      <Card
        size="small"
        className="transcodes-list-card"
        title={
          <div className="transcodes-tab-rail">
            <Segmented
              value={tab}
              onChange={(v) => {
                setTab(v)
                setPage(1)
              }}
              options={tabOptions}
            />
          </div>
        }
        extra={
          <DebouncedSearchInput
            placeholder="搜索番剧标题"
            onDebouncedChange={(v) => {
              setKeyword(v)
              setPage(1)
            }}
          />
        }
      >
        <div className="admin-table-wrap">
          <Table<TranscodeRow>
            rowKey="episode_id"
            size="small"
            loading={
              listQuery.isLoading ||
              (listQuery.isFetching && listQuery.isPlaceholderData)
            }
            columns={columns}
            dataSource={rows}
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: emptyContent,
            }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              onChange: setPage,
            }}
          />
        </div>

        <AdminMobileList
          rows={rows}
          rowKey={(row) => row.episode_id}
          loading={
            listQuery.isLoading ||
            (listQuery.isFetching && listQuery.isPlaceholderData)
          }
          emptyContent={emptyContent}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          renderItem={(row) => (
            <article className="admin-mobile-record">
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__title">
                  <Link
                    to="/anime/$animeId/episodes"
                    params={{ animeId: String(row.anime_id) }}
                  >
                    {row.anime_title ?? `番剧 #${row.anime_id}`}
                  </Link>
                  <div className="admin-mobile-record__meta">
                    <span>
                      第 {row.episode_number} 集
                      {row.episode_title ? ` · ${row.episode_title}` : ''}
                    </span>
                  </div>
                </div>
                <Tag color={TRANSCODE_STATUS_COLORS[row.status]}>
                  {TRANSCODE_STATUS_LABELS[row.status]}
                </Tag>
              </div>
              <div className="admin-mobile-record__badges">
                <Tag>{TIER_LABELS[row.admission_tier]}</Tag>
                {row.pinned ? <Tag color="gold">Pin</Tag> : null}
              </div>
              {row.status === 'failed' && row.last_error ? (
                <div className="admin-mobile-record__body">
                  <Typography.Paragraph ellipsis={{ rows: 2 }}>
                    {row.last_error}
                  </Typography.Paragraph>
                </div>
              ) : null}
              <div className="admin-mobile-record__stats">
                <span>
                  体积 {formatBytes(row.total_bytes ?? row.source_bytes)}
                </span>
                <span>7 天命中 {row.hits_7d}</span>
                <span>尝试 {row.attempts}</span>
                <span>{formatCST(statusTime(row), 'MM-DD HH:mm')}</span>
              </div>
              {/* 卡片里用 confirm 替代 Popconfirm；Pin 作为常驻次要操作 */}
              <AdminRecordActions
                actions={[
                  {
                    key: 'retry',
                    label: '重试',
                    emphasis: true,
                    icon: <RedoOutlined />,
                    visible:
                      row.status === 'failed' || row.status === 'evicted',
                    disabled: enqueueMutation.isPending,
                    confirm: { title: '重新入队转码？', okText: '重试' },
                    onClick: () => enqueueMutation.mutate([row.episode_id]),
                  },
                  {
                    key: 'evict',
                    label: '移除',
                    icon: <DeleteOutlined />,
                    danger: true,
                    visible: row.status === 'ready',
                    disabled: evictMutation.isPending,
                    confirm: {
                      title: '移除转码产物？',
                      description:
                        '将删除 R2 产物并清除 Pin，由 worker 异步清理。',
                      okText: '移除',
                    },
                    onClick: () => evictMutation.mutate(row.episode_id),
                  },
                  row.pinned
                    ? {
                        key: 'unpin',
                        label: '解除 Pin',
                        emphasis: false,
                        icon: <PushpinOutlined />,
                        disabled: pinMutation.isPending,
                        onClick: () =>
                          pinMutation.mutate({
                            episodeId: row.episode_id,
                            pinned: false,
                            expiresAt: null,
                          }),
                      }
                    : {
                        key: 'pin',
                        label: 'Pin',
                        emphasis: false,
                        icon: <PushpinOutlined />,
                        disabled: pinMutation.isPending,
                        onClick: () => setPinTarget(row),
                      },
                ]}
              />
            </article>
          )}
        />
      </Card>

      {/* ── Pin 弹窗 ── */}
      <PinModal
        key={pinTarget?.episode_id}
        target={pinTarget}
        loading={pinMutation.isPending}
        onCancel={() => setPinTarget(null)}
        onOk={(expiresAt) =>
          pinTarget &&
          pinMutation.mutate({
            episodeId: pinTarget.episode_id,
            pinned: true,
            expiresAt,
          })
        }
      />
    </Space>
  )
}

/* ── Pin 设置弹窗：永久 or 到期自动失效 ── */

function PinModal(props: {
  target: TranscodeRow | null
  loading: boolean
  onCancel: () => void
  onOk: (expiresAt: string | null) => void
}) {
  const [mode, setMode] = useState<'forever' | 'until'>('forever')
  const [until, setUntil] = useState<Dayjs | null>(null)

  return (
    <Modal
      title={
        props.target
          ? `Pin 豁免 —— ${props.target.anime_title ?? ''} 第 ${props.target.episode_number} 集`
          : 'Pin 豁免'
      }
      open={props.target != null}
      confirmLoading={props.loading}
      okText="设置 Pin"
      okButtonProps={{ disabled: mode === 'until' && !until }}
      onCancel={props.onCancel}
      onOk={() =>
        props.onOk(mode === 'forever' ? null : (until?.toISOString() ?? null))
      }
      destroyOnHidden
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">
          Pin 后该集产物不参与预算驱逐（管理员手动移除不受此限制）。
        </Typography.Text>
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value as 'forever' | 'until')}
          options={[
            { value: 'forever', label: '永久' },
            { value: 'until', label: '到期自动失效' },
          ]}
        />
        {mode === 'until' && (
          <DatePicker
            showTime
            value={until}
            onChange={setUntil}
            // 按天禁用：今天仍可选（选当天晚些时候过期是合法场景）
            disabledDate={(d) => d.isBefore(dayjs(), 'day')}
            style={{ width: '100%' }}
          />
        )}
      </Space>
    </Modal>
  )
}
